import { Box, Flex, IconArrowRight, IconDelete, IconOrganizationSite16, ImageButton, Tooltip, toast } from '@gmg/gmg-react-components';
import { FC, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import AppContext from '../../AppContext';
import { useDeleteOrganization } from '../../graphql/useDeleteOrganization';
import { getErrorMsgForToast } from '../toastErrorHandler';
import AssignSitesToOrgModalContainer, { SiteAssignmentCandidate } from './AssignSitesToOrgModalContainer';

export interface ActionProps {
    orgId: string;
    orgName: string;
    isSiteOrganization: boolean;
    siteAssignmentCandidates: Array<SiteAssignmentCandidate>;
};

const OrganizationActions: FC<ActionProps> = (props) => {
    const context = useContext(AppContext);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const deleteOrganization = useDeleteOrganization(props.orgId);

    const handleDelete = () => {
        const pendingPromise = deleteOrganization.mutateAsync();

        void toast.promise(pendingPromise, {
            loading: t('Common.Toast.Loading', 'Loading'),
            success: t('OrganizationsActions.delete.toast_success', {
                defaultValue: 'You successfully deleted the organization {{organizationName}}',
                organizationName: props.orgName,
            }),
            error: response => getErrorMsgForToast(response, t),
        });
    };

    const handleDeleteIconClick = (ev: any) => {
        ev.stopPropagation();
        context.onShowModal('infoModal', {
            title: t('OrganizationsActions.delete.modalTitle_title', 'Delete Organization'),
            content: <Box>
                <p>{t('OrganizationsActions.delete.content_desc', {
                    defaultValue: 'Are you sure you want to delete the organization {{organizationName}}?',
                    organizationName: props.orgName,
                })}</p>
                <p className="px10_400_weak">{t('OrganizationsActions.delete.info_desc', 'You can delete only organizations that have no user groups, folders and no associated users. This action cannot be undone.')}</p>
            </Box>,
            onOkClick: handleDelete,
            btnOkCaption: t('ButtonTitles.action_delete_btn', 'Delete'),
        });
    };

    const handleAssignSitesIconClick = (ev: any) => {
        ev.stopPropagation();
        context.onShowModal('inputModal', {
            title: t('AssignSitesToOrgModal.modal_title', 'Assign sites'),
            titleIcon: <IconOrganizationSite16/>,
            content: <AssignSitesToOrgModalContainer currentOrgId={props.orgId} currentOrgName={props.orgName} organizations={props.siteAssignmentCandidates} />,
        });
    };

    return (
        <Flex className="invisibleSVG" justifyContent='flex-end' gap='16px'>
            {context.featureFlags.showSiteAssignment && !props.isSiteOrganization && (
                <Tooltip message={t('OrganizationsActions.assignSiteOrg.tooltip', 'Assign site organization')}>
                    <ImageButton
                        icon={<IconOrganizationSite16 />}
                        onClick={handleAssignSitesIconClick}
                        aria-label="assign site organization icon"
                    />
                </Tooltip>
            )}
            <Tooltip message={t('ButtonTitles.action_delete_btn')}>
                <ImageButton
                    icon={<IconDelete />}
                    onClick={handleDeleteIconClick}
                    aria-label="delete icon"
                />
            </Tooltip>
            <ImageButton
                icon={<IconArrowRight />}
                aria-label="Open File icon"
                onClick={(e) => {
                    e.stopPropagation();
                    navigate(`${props.orgId}`);
                }}
            />
        </Flex>
    );
};

export default OrganizationActions;
